// RoleBulkModal — bulk Assign/Remove role for the selected people. One modal
// parametrized by `variant`: assign (success tone, add/replace choice) or remove
// (danger tone, no choice). Picks role ids via toggle chips and hands them back
// to PeoplePage, which POSTs company-users/bulk-action. The caller decides what
// `roles` holds — every company role for assign, only the roles the targets
// actually hold for remove.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Icon, Modal } from '@/components/common';
import type { BulkActionMethod } from '@/api/endpoints/peopleApi';

interface RoleOption {
  id: string;
  title: string;
}

interface RoleBulkModalProps {
  open: boolean;
  variant: 'assign' | 'remove';
  peopleCount: number;
  /** What the picker offers — scoped by the caller to the variant. */
  roles: RoleOption[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: (roleIds: string[], method?: BulkActionMethod) => void;
}

const RoleBulkModal = ({
  open,
  variant,
  peopleCount,
  roles,
  loading,
  onClose,
  onConfirm,
}: RoleBulkModalProps) => {
  const { t } = useTranslation();
  const assign = variant === 'assign';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<BulkActionMethod>('add');

  const selectedIds = useMemo(() => [...selected], [selected]);
  // One label for the pill group's heading and its aria-label — they describe
  // the same control, so they can't be allowed to drift.
  const pickerLabel = t(assign ? 'people_bulk_roles_to_assign' : 'people_bulk_roles_to_remove');

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    if (!selected.size || loading) return;
    onConfirm(selectedIds, assign ? method : undefined);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      tone={assign ? 'success' : 'danger'}
      icon={assign ? 'shield-plus' : 'shield-minus'}
      title={t(assign ? 'people_bulk_assign_title' : 'people_bulk_remove_title')}
      subtitle={t(assign ? 'people_bulk_assign_subtitle' : 'people_bulk_remove_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>
            {t('button_cancel')}
          </Button>
          <Button onClick={submit} disabled={!selected.size} loading={loading}>
            {t('button_continue')}
          </Button>
        </>
      }
    >
      <Chip variant='neutral' iconStart='users' className='people-bulk__count'>
        {t('people_bulk_people_selected', { count: peopleCount })}
      </Chip>

      {assign && (
        <fieldset className='people-bulk__section' role='radiogroup'>
          <legend className='people-bulk__section-head'>
            <Icon name='sliders-horizontal' size={15} />
            {t('people_bulk_how_to_apply')}
          </legend>
          <div className='people-bulk__choices'>
            {(
              [
                { value: 'add', title: 'people_bulk_add_roles', hint: 'people_bulk_add_roles_hint' },
                {
                  value: 'replace',
                  title: 'people_bulk_replace_roles',
                  hint: 'people_bulk_replace_roles_hint',
                },
              ] as const
            ).map((opt) => (
              <label
                key={opt.value}
                className={`people-bulk__choice${method === opt.value ? ' is-selected' : ''}`}
              >
                <input
                  type='radio'
                  name='people-bulk-how-to-apply'
                  className='app-radio__input'
                  checked={method === opt.value}
                  onChange={() => setMethod(opt.value)}
                />
                <span className='app-radio__circle' aria-hidden='true' />
                <span className='people-bulk__choice-text'>
                  <span className='people-bulk__choice-title'>{t(opt.title)}</span>
                  <span className='people-bulk__choice-hint'>{t(opt.hint)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className='people-bulk__section'>
        <p className='people-bulk__section-head'>
          <Icon name={assign ? 'shield-check' : 'shield-minus'} size={15} />
          {pickerLabel}
          <span className='people-bulk__section-hint'>{t('people_bulk_select_one_plus')}</span>
        </p>
        {roles.length === 0 ? (
          <p className='people-bulk__empty'>
            {t(assign ? 'people_bulk_pick_empty' : 'people_bulk_remove_empty')}
          </p>
        ) : (
          <div className='people-bulk__pills' role='group' aria-label={pickerLabel}>
            {roles.map((role) => {
              const on = selected.has(role.id);
              // A pick reads as what it will do: brand + check when assigning,
              // danger + x when removing.
              const onClass = assign ? ' people-role-pill--selected' : ' people-role-pill--danger';
              return (
                <button
                  key={role.id}
                  type='button'
                  className={`people-role-pill${on ? onClass : ''}`}
                  aria-pressed={on}
                  onClick={() => toggle(role.id)}
                >
                  <Icon name={on ? (assign ? 'check' : 'x') : 'shield-check'} size={15} />
                  {role.title}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RoleBulkModal;
